document.documentElement.classList.remove('no-js');

(() => {
  const money = (cents) => {
    const format = window.EverwordTheme?.moneyFormat || '${{amount}}';
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount).replace(/<[^>]+>/g, '');
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#039;','"':'&quot;'}[char]));
  const rootUrl = window.EverwordTheme?.rootUrl || '/';
  const freeShippingThreshold = Number(window.EverwordTheme?.freeShippingThreshold || 0);
  const subtleMotion = window.EverwordTheme?.subtleMotion !== false;

  document.addEventListener('DOMContentLoaded', () => {
    const syncScrollLock = () => {
      const overlayOpen = document.querySelector('[data-menu-drawer]:not([hidden]), [data-cart-drawer]:not([hidden]), [data-search-drawer]:not([hidden]), [data-contact-drawer]:not([hidden]), [data-product-zoom]:not([hidden])');
      document.body.classList.toggle('is-scroll-locked', Boolean(overlayOpen));
    };

    /* Compact sticky header */
    const siteHeader = document.querySelector('[data-site-header]');
    const updateHeaderState = () => {
      if (!siteHeader) return;
      siteHeader.classList.toggle('is-scrolled', window.scrollY > 42);
      document.documentElement.style.setProperty('--ew-header-bottom', `${Math.round(siteHeader.getBoundingClientRect().bottom)}px`);
    };
    updateHeaderState();
    window.addEventListener('scroll', updateHeaderState, { passive: true });
    window.addEventListener('resize', updateHeaderState, { passive: true });

    document.querySelectorAll('[data-announcement]').forEach(bar => {
      const messages = [...bar.querySelectorAll('[data-announcement-message]')];
      if (messages.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const delay = Math.max(4000, Number(bar.dataset.interval || 6000));
      let active = 0;
      let timer;
      const rotate = () => {
        messages[active].classList.remove('is-active');
        active = (active + 1) % messages.length;
        messages[active].classList.add('is-active');
      };
      const start = () => { window.clearInterval(timer); timer = window.setInterval(rotate, delay); };
      const stop = () => window.clearInterval(timer);
      bar.addEventListener('pointerenter', stop);
      bar.addEventListener('pointerleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);
      start();
    });

    /* Mobile navigation */
    const drawer = document.querySelector('[data-menu-drawer]');
    const open = document.querySelector('[data-menu-open]');
    const close = document.querySelector('[data-menu-close]');
    const setDrawer = (isOpen) => {
      if (!drawer) return;
      const wasOpen = !drawer.hidden;
      drawer.hidden = !isOpen;
      open?.setAttribute('aria-expanded', String(isOpen));
      syncScrollLock();
      if (isOpen) close?.focus();
      else if (wasOpen) open?.focus();
    };
    open?.addEventListener('click', () => setDrawer(true));
    close?.addEventListener('click', () => setDrawer(false));
    drawer?.addEventListener('click', e => { if (e.target === drawer) setDrawer(false); });
    drawer?.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll('a[href], button, summary, input, select, textarea, [tabindex="0"]')].filter(item => item.getClientRects().length && !item.disabled);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    });
    window.matchMedia('(min-width: 990px)').addEventListener('change', event => { if (event.matches) setDrawer(false); });

    const contactDrawer = document.querySelector('[data-contact-drawer]');
    const contactResult = contactDrawer?.querySelector('[data-contact-result]');
    let contactTrigger = null;
    const setContactDrawer = (isOpen, trigger = null) => {
      if (!contactDrawer) return;
      const wasOpen = !contactDrawer.hidden;
      if (isOpen && trigger) contactTrigger = trigger;
      contactDrawer.hidden = !isOpen;
      contactDrawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      document.querySelectorAll('[data-contact-open]').forEach(button => button.setAttribute('aria-expanded', isOpen ? 'true' : 'false'));
      syncScrollLock();
      if (isOpen) window.setTimeout(() => (contactResult || contactDrawer.querySelector('input, select, textarea'))?.focus(), 80);
      else if (wasOpen) contactTrigger?.focus();
    };
    document.querySelectorAll('[data-contact-open]').forEach(trigger => trigger.addEventListener('click', () => {
      setDrawer(false);
      setContactDrawer(true, trigger);
    }));
    contactDrawer?.querySelectorAll('[data-contact-close]').forEach(button => button.addEventListener('click', () => setContactDrawer(false)));
    if (contactResult) setContactDrawer(true);

    /* Predictive search drawer */
    const searchDrawer = document.querySelector('[data-search-drawer]');
    const searchInput = searchDrawer?.querySelector('[data-predictive-search-input]');
    const searchResults = searchDrawer?.querySelector('[data-predictive-search-results]');
    const searchStatus = searchDrawer?.querySelector('[data-predictive-search-status]');
    const searchAll = searchDrawer?.querySelector('[data-predictive-search-all]');
    let searchTimer;

    const setSearchDrawer = (isOpen) => {
      if (!searchDrawer) return;
      searchDrawer.hidden = !isOpen;
      searchDrawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      syncScrollLock();
      if (isOpen) window.setTimeout(() => searchInput?.focus(), 80);
    };
    document.querySelectorAll('[data-search-open]').forEach(btn => btn.addEventListener('click', () => setSearchDrawer(true)));
    searchDrawer?.querySelectorAll('[data-search-close]').forEach(btn => btn.addEventListener('click', () => setSearchDrawer(false)));

    const renderSearchResults = (payload, term) => {
      if (!searchResults || !searchStatus) return;
      const products = payload?.resources?.results?.products || [];
      const collections = payload?.resources?.results?.collections || [];
      const productMarkup = products.slice(0, 6).map(product => {
        const image = product.featured_image?.url || product.image || '';
        const displayPrice = typeof product.price === 'number' ? money(product.price) : (product.price || '');
        return `<a class="ew-search-result" href="${product.url}">${image ? `<img src="${image}" alt="" loading="lazy">` : ''}<span><strong>${escapeHtml(product.title)}</strong><span>${escapeHtml(displayPrice)}</span></span></a>`;
      }).join('');
      const collectionMarkup = collections.slice(0, 3).map(collection => `<a class="ew-search-collection" href="${collection.url}">${escapeHtml(collection.title)} →</a>`).join('');
      searchResults.innerHTML = productMarkup + collectionMarkup;
      searchStatus.textContent = products.length || collections.length ? `Results for “${term}”` : `No results yet for “${term}”.`;
      if (searchAll) searchAll.href = `${rootUrl}search?q=${encodeURIComponent(term)}&type=product`;
    };

    searchInput?.addEventListener('input', () => {
      const term = searchInput.value.trim();
      window.clearTimeout(searchTimer);
      if (term.length < 2) {
        if (searchResults) searchResults.innerHTML = '';
        if (searchStatus) searchStatus.textContent = 'Start typing to explore gifts, recipients, and collections.';
        return;
      }
      searchTimer = window.setTimeout(async () => {
        try {
          if (searchStatus) searchStatus.textContent = 'Searching…';
          const endpoint = `${rootUrl}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last`;
          const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
          renderSearchResults(await response.json(), term);
        } catch (e) {
          if (searchStatus) searchStatus.textContent = 'Search is temporarily unavailable.';
        }
      }, 180);
    });

    /* Collection helpers */
    document.querySelectorAll('[data-auto-submit]').forEach(el => el.addEventListener('change', () => el.form?.submit()));
    const toolbar = document.querySelector('.collection-toolbar');
    document.querySelector('[data-filter-toggle]')?.addEventListener('click', () => toolbar?.classList.toggle('is-open'));

    /* Cart drawer */
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    const cartItems = cartDrawer?.querySelector('[data-cart-drawer-items]');
    const cartFooter = cartDrawer?.querySelector('[data-cart-drawer-footer]');
    const cartCount = cartDrawer?.querySelector('[data-cart-drawer-count]');
    const cartSubtotal = cartDrawer?.querySelector('[data-cart-drawer-subtotal]');
    const shippingProgress = cartDrawer?.querySelector('[data-free-shipping-progress]');
    const shippingMessage = cartDrawer?.querySelector('[data-free-shipping-message]');
    const shippingFill = cartDrawer?.querySelector('[data-free-shipping-fill]');

    const setCartDrawer = (isOpen) => {
      if (!cartDrawer) return;
      cartDrawer.hidden = !isOpen;
      cartDrawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      syncScrollLock();
    };

    const updateHeaderCartCount = (count) => {
      document.querySelectorAll('.header-cart').forEach(link => {
        let badge = link.querySelector('.header-cart__count');
        if (count > 0) {
          if (!badge) { badge = document.createElement('span'); badge.className = 'header-cart__count'; link.appendChild(badge); }
          badge.textContent = count;
        } else badge?.remove();
      });
    };

    const updateFreeShipping = (total) => {
      if (!shippingProgress || freeShippingThreshold <= 0) return;
      shippingProgress.hidden = false;
      const remaining = Math.max(0, freeShippingThreshold - Number(total || 0));
      const percent = Math.min(100, (Number(total || 0) / freeShippingThreshold) * 100);
      if (shippingFill) shippingFill.style.width = `${percent}%`;
      if (shippingMessage) shippingMessage.textContent = remaining > 0 ? `You’re ${money(remaining)} away from free shipping.` : 'You’ve unlocked free shipping.';
    };

    const cartPropertiesMarkup = (properties = {}) => Object.entries(properties)
      .filter(([key, value]) => value && !String(key).startsWith('_'))
      .map(([key, value]) => {
        const raw = String(value);
        const display = raw.includes('/uploads/')
          ? `<a href="${escapeHtml(raw)}" target="_blank" rel="noopener">View uploaded file</a>`
          : escapeHtml(raw);
        return `<span>${escapeHtml(key)}: ${display}</span>`;
      }).join('');

    const refreshCart = async () => {
      try {
        const response = await fetch(`${rootUrl}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await response.json();
        updateHeaderCartCount(cart.item_count);
        if (!cartDrawer) return cart;
        if (cartCount) cartCount.textContent = cart.item_count ? `(${cart.item_count})` : '';
        if (cartSubtotal) cartSubtotal.textContent = money(cart.total_price);
        if (cartFooter) cartFooter.hidden = cart.item_count === 0;
        updateFreeShipping(cart.total_price);

        if (cartItems) {
          if (!cart.item_count) {
            cartItems.innerHTML = '<p class="ew-cart-drawer__empty">Your cart is empty.</p>';
          } else {
            cartItems.innerHTML = cart.items.map(item => {
              const image = item.featured_image?.url || item.image || '';
              const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<span>${escapeHtml(item.variant_title)}</span>` : '';
              const properties = cartPropertiesMarkup(item.properties || {});
              return `<article class="ew-cart-line" data-cart-line="${escapeHtml(item.key)}">
                ${image ? `<a href="${item.url}"><img src="${image}" alt="" loading="lazy"></a>` : ''}
                <div><strong><a href="${item.url}">${escapeHtml(item.product_title)}</a></strong>${variant}${properties ? `<div class="ew-cart-line__properties">${properties}</div>` : ''}<b>${money(item.final_line_price)}</b>
                <div class="ew-cart-line__controls" aria-label="Quantity controls"><button type="button" data-cart-quantity-change data-cart-key="${escapeHtml(item.key)}" data-cart-quantity="${Math.max(0,item.quantity-1)}" aria-label="Decrease quantity">−</button><output>${item.quantity}</output><button type="button" data-cart-quantity-change data-cart-key="${escapeHtml(item.key)}" data-cart-quantity="${item.quantity+1}" aria-label="Increase quantity">+</button><button class="ew-cart-line__remove" type="button" data-cart-quantity-change data-cart-key="${escapeHtml(item.key)}" data-cart-quantity="0">Remove</button></div></div>
              </article>`;
            }).join('');
          }
        }
        return cart;
      } catch (e) { return null; }
    };

    const changeCartQuantity = async (key, quantity) => {
      try {
        await fetch(`${rootUrl}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: Number(quantity) }) });
        await refreshCart();
      } catch (e) {}
    };

    cartDrawer?.addEventListener('click', event => {
      const control = event.target.closest('[data-cart-quantity-change]');
      if (!control) return;
      changeCartQuantity(control.dataset.cartKey, control.dataset.cartQuantity);
    });

    document.querySelectorAll('[data-cart-open]').forEach(trigger => trigger.addEventListener('click', async event => {
      if (!cartDrawer) return;
      event.preventDefault();
      await refreshCart();
      setCartDrawer(true);
    }));
    document.querySelectorAll('[data-cart-close]').forEach(trigger => trigger.addEventListener('click', () => setCartDrawer(false)));

    document.addEventListener('click', event => {
      const opener = event.target.closest('[data-quick-view-open]');
      if (opener) {
        const dialog = opener.closest('.product-card')?.querySelector('[data-quick-view]');
        if (dialog?.showModal) dialog.showModal();
        return;
      }
      const close = event.target.closest('[data-quick-view-close]');
      if (close) close.closest('[data-quick-view]')?.close();
      if (event.target.matches?.('[data-quick-view]')) event.target.close();
    });

    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      setDrawer(false); setCartDrawer(false); setSearchDrawer(false); setContactDrawer(false);
      document.querySelectorAll('[data-product-zoom]:not([hidden])').forEach(z => { z.hidden = true; z.setAttribute('aria-hidden', 'true'); });
      syncScrollLock();
    });

    /* Product page */
    const addBusinessDays = (start, days) => {
      const date = new Date(start);
      let remaining = Math.max(0, Number(days) || 0);
      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        const weekday = date.getDay();
        if (weekday !== 0 && weekday !== 6) remaining -= 1;
      }
      return date;
    };
    const deliveryDateFormatter = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { month: 'short', day: 'numeric' });
    document.querySelectorAll('[data-delivery-window]').forEach(windowEl => {
      const minDays = Number(windowEl.dataset.minDays || 0);
      const maxDays = Number(windowEl.dataset.maxDays || 0);
      if (!minDays || !maxDays) return;
      const today = new Date();
      today.setHours(12, 0, 0, 0);
      const earliest = addBusinessDays(today, minDays);
      const latest = addBusinessDays(today, maxDays);
      windowEl.textContent = `${deliveryDateFormatter.format(earliest)}–${deliveryDateFormatter.format(latest)}`;
    });

    document.querySelectorAll('[data-product-root]').forEach(root => {
      const json = root.querySelector('[data-product-variants]');
      let variants = [];
      try { variants = json ? JSON.parse(json.textContent) : []; } catch (e) { variants = []; }


      // Keep this product's review snippets inline and make its rating a direct link.
      const reviewSnippets = root.querySelector('loox-snippets-widget[product-id="8757630337185"]');
      if (reviewSnippets) {
        const reviewRegion = reviewSnippets.closest('.shopify-app-block') || reviewSnippets;
        reviewRegion.id = 'everword-monogram-reviews';
        reviewRegion.setAttribute('role', 'region');
        reviewRegion.setAttribute('aria-label', 'Customer reviews');
        reviewRegion.setAttribute('tabindex', '-1');
        root.querySelectorAll('a').forEach(link => {
          if (link.querySelector('.loox-rating')) link.setAttribute('href', '#everword-monogram-reviews');
        });
        root.addEventListener('click', event => {
          const link = event.target.closest?.('a');
          if (!link?.querySelector('.loox-rating')) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          reviewRegion.focus({ preventScroll: true });
          reviewRegion.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        }, true);

        const stopReviewExpansion = event => {
          if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
          const isReviewCard = event.composedPath().some(node => node.matches?.('button.quickview-button'));
          if (!isReviewCard) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        };
        reviewSnippets.addEventListener('click', stopReviewExpansion, true);
        reviewSnippets.addEventListener('keydown', stopReviewExpansion, true);

        // Keep noninteractive review cards out of the tab order; arrows remain buttons.
        customElements.whenDefined('loox-snippets-widget').then(() => {
          const shadow = reviewSnippets.shadowRoot;
          if (!shadow) return;
          const makeCardsReadable = () => {
            shadow.querySelectorAll('button.quickview-button').forEach(card => {
              if (card.getAttribute('role') !== 'group') card.setAttribute('role', 'group');
              if (card.getAttribute('tabindex') !== '-1') card.setAttribute('tabindex', '-1');
            });
            if (!shadow.querySelector('#everword-inline-review-style')) {
              const style = document.createElement('style');
              style.id = 'everword-inline-review-style';
              style.textContent = '.quickview-button { cursor: default !important; }';
              shadow.appendChild(style);
            }
          };
          makeCardsReadable();
          new MutationObserver(makeCardsReadable).observe(shadow, {
            childList: true, subtree: true, attributes: true, attributeFilter: ['tabindex', 'role']
          });
        });
      }

      const radios = [...root.querySelectorAll('.variant-fieldset input[type="radio"]')];
      const idInput = root.querySelector('[data-variant-id]');
      const addButton = root.querySelector('[data-add-button]');
      const priceWrap = root.querySelector('[data-price-wrapper]');
      const stickyBar = root.querySelector('[data-sticky-atc]');
      const stickyAdd = root.querySelector('[data-sticky-add]');
      const stickyPrice = root.querySelector('[data-sticky-price]');
      const stickyVariantTitle = root.querySelector('[data-sticky-variant-title]');
      const mainForm = root.querySelector('form.product-form');
      const panels = [...root.querySelectorAll('[data-media-id]')];
      const thumbs = [...root.querySelectorAll('[data-media-target]')];

      const activateMedia = id => {
        const panelIndex = panels.findIndex(p => String(p.dataset.mediaId) === String(id));
        if (panelIndex < 0) return;
        panels.forEach((panel,index) => {
          const active = index === panelIndex;
          if (!active) panel.querySelectorAll('video').forEach(video => video.pause());
          panel.hidden = !active;
          panel.classList.toggle('is-active', active);
        });
        const counter = root.querySelector('[data-gallery-count]');
        if (counter) counter.textContent = `${panelIndex + 1} / ${panels.length}`;
        thumbs.forEach(btn => { const active = String(btn.dataset.mediaTarget) === String(id); btn.classList.toggle('is-active', active); btn.setAttribute('aria-current', active ? 'true' : 'false'); });
      };
      thumbs.forEach(btn => btn.addEventListener('click', () => activateMedia(btn.dataset.mediaTarget)));
      const galleryStage = root.querySelector('[data-gallery-stage]');
      const stepMedia = direction => {
        if (panels.length < 2) return;
        const current = panels.findIndex(panel => !panel.hidden);
        const next = (current + direction + panels.length) % panels.length;
        activateMedia(panels[next].dataset.mediaId);
        const thumb = thumbs[next], track = thumb?.parentElement;
        if (thumb && track) {
          if (track.scrollWidth > track.clientWidth) track.scrollLeft = thumb.offsetLeft - track.offsetLeft - (track.clientWidth - thumb.clientWidth) / 2;
          else track.scrollTop = thumb.offsetTop - track.offsetTop - (track.clientHeight - thumb.clientHeight) / 2;
        }
      };
      root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => stepMedia(-1));
      root.querySelector('[data-gallery-next]')?.addEventListener('click', () => stepMedia(1));
      galleryStage?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.closest('video, iframe, model-viewer, input, textarea')) return;
        event.preventDefault(); stepMedia(event.key === 'ArrowRight' ? 1 : -1);
      });
      let swipeStart = null, suppressZoomUntil = 0;
      galleryStage?.addEventListener('touchstart', event => {
        swipeStart = event.touches.length === 1 && !event.target.closest('video, iframe, model-viewer, [data-gallery-prev], [data-gallery-next]')
          ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { passive: true });
      galleryStage?.addEventListener('touchmove', event => { if (event.touches.length !== 1) swipeStart = null; }, { passive: true });
      galleryStage?.addEventListener('touchcancel', () => { swipeStart = null; }, { passive: true });
      galleryStage?.addEventListener('touchend', event => {
        if (!swipeStart || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - swipeStart.x, dy = event.changedTouches[0].clientY - swipeStart.y;
        swipeStart = null;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        suppressZoomUntil = Date.now() + 450;
        stepMedia(dx < 0 ? 1 : -1);
      }, { passive: true });
      galleryStage?.addEventListener('click', event => {
        if (Date.now() < suppressZoomUntil && event.target.closest('[data-zoom-open]')) { event.preventDefault(); event.stopPropagation(); }
      }, true);

      const currentSelectedOptions = () => [...root.querySelectorAll('.variant-fieldset')].map(fieldset => fieldset.querySelector('input:checked')?.value || '');
      const findVariant = () => {
        if (!variants.length) return null;
        if (!radios.length) return variants[0];
        const selected = currentSelectedOptions();
        return variants.find(v => v.options.every((opt,index) => opt === selected[index]));
      };

      // This media belongs only to the Custom Monogram product.
      const monogramPackagingMediaId = '38109547102369';
      const hasMonogramPackaging = panels.some(panel => panel.dataset.mediaId === monogramPackagingMediaId);
      const showOptionMedia = radio => {
        const variant = findVariant();
        const optionIndex = radio?.closest('[data-option-index]')?.dataset.optionIndex;
        const mediaId = hasMonogramPackaging && optionIndex === '1'
          ? monogramPackagingMediaId
          : variant?.featured_media?.id;
        if (mediaId) activateMedia(mediaId);
      };

      const updateVariant = event => {
        const variant = findVariant();
        if (!variant) {
          if (addButton) { addButton.disabled = true; addButton.textContent = 'Unavailable'; }
          if (stickyAdd) { stickyAdd.disabled = true; stickyAdd.textContent = 'Unavailable'; }
          return;
        }
        if (idInput) idInput.value = variant.id;
        if (addButton) { addButton.disabled = !variant.available; addButton.textContent = variant.available ? 'Add to cart' : 'Sold out'; }
        if (stickyAdd) { stickyAdd.disabled = !variant.available; stickyAdd.textContent = variant.available ? `Add to cart — ${money(variant.price)}` : 'Sold out'; }
        if (priceWrap) priceWrap.textContent = money(variant.price);
        if (stickyPrice) stickyPrice.textContent = money(variant.price);
        if (stickyVariantTitle) stickyVariantTitle.textContent = variant.title === 'Default Title' ? '' : variant.title;
        showOptionMedia(event?.target);
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url);
      };
      radios.forEach(r => {
        r.addEventListener('change', updateVariant);
        // Clicking an already selected choice should still reveal its photo.
        if (hasMonogramPackaging) r.addEventListener('click', () => showOptionMedia(r));
      });
      if (hasMonogramPackaging) showOptionMedia(null);

      /* Artwork Swapper customer fields (native Shopify line item properties). */
      root.querySelectorAll('[data-artwork-customizer]').forEach(customizer => {
        customizer.querySelectorAll('[data-customizer-text]').forEach(textInput => {
          const field = textInput.closest('[data-customizer-field]');
          const count = field?.querySelector('[data-customizer-count]');
          const updateCount = () => {
            if (!count) return;
            const max = textInput.maxLength > 0 ? textInput.maxLength : '';
            count.textContent = max ? `${textInput.value.length}/${max}` : `${textInput.value.length}`;
          };
          textInput.addEventListener('input', updateCount);
          updateCount();
        });

        customizer.querySelectorAll('[data-customizer-photo]').forEach(photoInput => {
          const field = photoInput.closest('[data-customizer-field]');
          const preview = field?.querySelector('[data-customizer-preview]');
          const previewImage = field?.querySelector('[data-customizer-preview-image]');
          const fileName = field?.querySelector('[data-customizer-file-name]');
          const remove = field?.querySelector('[data-customizer-remove]');
          const error = field?.querySelector('[data-customizer-error]');
          let objectUrl = '';

          const showError = message => {
            photoInput.setCustomValidity(message || '');
            if (error) {
              error.textContent = message || '';
              error.hidden = !message;
            }
          };
          const clearPreview = ({ clearInput = true } = {}) => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
            objectUrl = '';
            if (clearInput) photoInput.value = '';
            if (preview) preview.hidden = true;
            if (previewImage) previewImage.removeAttribute('src');
            if (fileName) fileName.textContent = '';
            showError('');
          };

          photoInput.addEventListener('change', () => {
            const file = photoInput.files?.[0];
            if (!file) { clearPreview(); return; }
            showError('');

            const maxMb = Number(photoInput.dataset.maxMb || 0);
            const minWidth = Number(photoInput.dataset.minWidth || 0);
            const minHeight = Number(photoInput.dataset.minHeight || 0);
            if (maxMb > 0 && file.size > maxMb * 1024 * 1024) {
              clearPreview({ clearInput: false });
              showError(`Please choose a file under ${maxMb} MB.`);
              photoInput.reportValidity();
              return;
            }

            if (objectUrl) URL.revokeObjectURL(objectUrl);
            objectUrl = URL.createObjectURL(file);
            const finishPreview = () => {
              if (previewImage) previewImage.src = objectUrl;
              if (fileName) fileName.textContent = file.name;
              if (preview) preview.hidden = false;
            };

            if ((minWidth > 0 || minHeight > 0) && file.type.startsWith('image/')) {
              const probe = new Image();
              probe.onload = () => {
                const tooNarrow = minWidth > 0 && probe.naturalWidth < minWidth;
                const tooShort = minHeight > 0 && probe.naturalHeight < minHeight;
                if (tooNarrow || tooShort) {
                  clearPreview({ clearInput: false });
                  const dimensions = [minWidth > 0 ? `${minWidth}px wide` : '', minHeight > 0 ? `${minHeight}px tall` : ''].filter(Boolean).join(' and ');
                  showError(`For best print quality, please upload an image at least ${dimensions}.`);
                  photoInput.reportValidity();
                  return;
                }
                finishPreview();
              };
              probe.onerror = () => finishPreview();
              probe.src = objectUrl;
            } else {
              finishPreview();
            }
          });
          remove?.addEventListener('click', () => clearPreview());
        });
      });

      /* Sticky CTA submits the native product form so ShineOn properties and other app fields are preserved. */
      stickyAdd?.addEventListener('click', () => {
        const variant = findVariant();
        if (!variant || !variant.available || !mainForm) return;
        if (idInput) idInput.value = variant.id;
        if (!mainForm.checkValidity()) {
          const invalid = mainForm.querySelector(':invalid') || document.querySelector(`[form="${CSS.escape(mainForm.id)}"]:invalid`);
          invalid?.focus();
          mainForm.reportValidity?.();
          return;
        }
        mainForm.requestSubmit();
      });

      if (stickyBar && addButton && 'IntersectionObserver' in window) {
        new IntersectionObserver(entries => stickyBar.classList.toggle('is-visible', !entries[0].isIntersecting), { threshold: 0.05 }).observe(addButton);
      }

      /* Mobile-friendly product description */
      root.querySelectorAll('[data-collapsible-description]').forEach(wrap => {
        const content = wrap.querySelector('[data-description-content]');
        const toggle = wrap.querySelector('[data-description-toggle]');
        if (!content || !toggle) return;
        const assess = () => {
          if (window.innerWidth > 749) { wrap.classList.remove('has-overflow','is-expanded'); toggle.hidden = true; return; }
          const overflow = content.scrollHeight > content.clientHeight + 8;
          wrap.classList.toggle('has-overflow', overflow); toggle.hidden = !overflow;
        };
        toggle.addEventListener('click', () => { const expanded = wrap.classList.toggle('is-expanded'); toggle.textContent = expanded ? 'Read less' : 'Read more'; toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false'); });
        window.setTimeout(assess, 60); window.addEventListener('resize', assess, { passive: true });
      });

      /* Product media viewer */
      const zoom = root.querySelector('[data-product-zoom]');
      const zoomMedia = zoom?.querySelector('[data-zoom-media]');
      const zoomItems = [...root.querySelectorAll('[data-media-id]')];
      const zoomTriggers = [...root.querySelectorAll('[data-zoom-open]')];
      let zoomIndex = 0;
      
      const clearZoomMedia = () => {
        if (!zoomMedia) return;
        zoomMedia.querySelectorAll('video').forEach(video => video.pause());
        zoomMedia.replaceChildren();
      };
      
      const renderZoomMedia = panel => {
        if (!zoomMedia || !panel) return;
        clearZoomMedia();
      
        const imageTrigger = panel.querySelector('[data-zoom-open]');
        const sourceVideo = panel.querySelector('video');
        const sourceFrame = panel.querySelector('iframe');
        const sourceModel = panel.querySelector('model-viewer');
      
        if (imageTrigger) {
          const image = document.createElement('img');
          image.src = imageTrigger.dataset.zoomSrc;
          image.alt = imageTrigger.dataset.zoomAlt || '';
          zoomMedia.appendChild(image);
          return;
        }
      
        if (sourceVideo) {
          const video = sourceVideo.cloneNode(true);
          video.controls = false;
          video.muted = true;
          video.playsInline = true;
          video.setAttribute('playsinline', '');
          video.setAttribute('preload', 'metadata');
          zoomMedia.appendChild(video);
          video.load();
          if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            const playback = video.play();
            if (playback) playback.catch(() => {});
          }
          return;
        }
      
        if (sourceFrame) {
          const frame = sourceFrame.cloneNode(true);
          frame.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
          zoomMedia.appendChild(frame);
          return;
        }
      
        if (sourceModel) zoomMedia.appendChild(sourceModel.cloneNode(true));
      };
      
      const showZoomAt = index => {
        if (!zoom || !zoomMedia || !zoomItems.length) return;
        zoomIndex = (index + zoomItems.length) % zoomItems.length;
        root.querySelectorAll('.product-gallery video').forEach(video => video.pause());
        renderZoomMedia(zoomItems[zoomIndex]);
        zoom.hidden = false;
        zoom.setAttribute('aria-hidden', 'false');
        syncScrollLock();
      };
      
      const closeZoom = () => {
        if (!zoom) return;
        clearZoomMedia();
        zoom.hidden = true;
        zoom.setAttribute('aria-hidden', 'true');
        syncScrollLock();
      };
      
      zoomTriggers.forEach(trigger => {
        const panel = trigger.closest('[data-media-id]');
        const panelIndex = zoomItems.indexOf(panel);
        trigger.addEventListener('click', () => showZoomAt(panelIndex));
      });
      zoom?.querySelectorAll('[data-zoom-close]').forEach(button => button.addEventListener('click', closeZoom));
      zoom?.querySelector('[data-zoom-prev]')?.addEventListener('click', () => showZoomAt(zoomIndex - 1));
      zoom?.querySelector('[data-zoom-next]')?.addEventListener('click', () => showZoomAt(zoomIndex + 1));
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') clearZoomMedia();
      });

      /* Some app extensions calculate width only once. Nudge them after responsive layout settles. */
      window.setTimeout(() => window.dispatchEvent(new Event('resize')), 250);
    });

    /* Calm reveal motion */
    if (subtleMotion && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
      const targets = document.querySelectorAll('.recipient-section,.section-heading-row,.product-card,.promo-card,.trust-strip,.collection-hero');
      targets.forEach(el => el.classList.add('ew-reveal'));
      const revealObserver = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); } }), { rootMargin: '0px 0px -5% 0px', threshold: 0.08 });
      targets.forEach(el => revealObserver.observe(el));
    }

    /* Product recommendations */
    document.querySelectorAll('[data-product-recommendations]').forEach(async section => {
      const url = section.dataset.url;
      if (!url || section.children.length > 0) return;
      try {
        const response = await fetch(url); const text = await response.text();
        const html = new DOMParser().parseFromString(text,'text/html').querySelector('[data-product-recommendations]');
        if (html?.innerHTML.trim()) section.innerHTML = html.innerHTML;
      } catch (e) {}
    });
  });
})();



(() => {
  const getVideoLabels = video => {
    const root = video.closest('[data-product-root]');
    return {
      play: root?.dataset.videoPlayLabel || 'Play video',
      pause: root?.dataset.videoPauseLabel || 'Pause video'
    };
  };

  const setButtonState = (video, button) => {
    const labels = getVideoLabels(video);
    const isPlaying = !video.paused && !video.ended;
    button.dataset.state = isPlaying ? 'playing' : 'paused';
    button.setAttribute('aria-label', isPlaying ? labels.pause : labels.play);
    const label = button.querySelector('.visually-hidden');
    if (label) label.textContent = isPlaying ? labels.pause : labels.play;
  };

  const togglePlayback = video => {
    if (video.paused || video.ended) {
      const playback = video.play();
      if (playback) playback.catch(() => {});
    } else {
      video.pause();
    }
  };

  const prepareVideo = video => {
    const existingButton = video.parentElement?.querySelector('.product-video-toggle');
    if (video.dataset.simpleControlsReady === 'true' && existingButton) return;
    video.dataset.simpleControlsReady = 'true';
    video.controls = false;
    video.removeAttribute('controls');
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('x-webkit-airplay', 'deny');
    video.disablePictureInPicture = true;
    video.disableRemotePlayback = true;
    video.draggable = false;
    video.setAttribute('draggable', 'false');

    const container = video.parentElement;
    if (!container) return;
    container.classList.add('has-simple-video-controls');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'product-video-toggle';
    button.innerHTML = '<span class="visually-hidden"></span>';
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      togglePlayback(video);
    });
    video.addEventListener('click', () => togglePlayback(video));
    ['play', 'pause', 'ended'].forEach(eventName => {
      video.addEventListener(eventName, () => setButtonState(video, button));
    });
    container.appendChild(button);
    setButtonState(video, button);
  };

  const prepareMedia = scope => {
    const media = [];
    if (scope.matches?.('img, video')) media.push(scope);
    scope.querySelectorAll?.('img, video').forEach(item => media.push(item));
    media.forEach(item => {
      if (!item.matches('video')) return;
      item.draggable = false;
      item.setAttribute('draggable', 'false');
      if (item.closest('[data-product-gallery], [data-product-zoom]')) {
        prepareVideo(item);
      }
    });
  };

  const initializeProductMedia = scope => {
    prepareMedia(scope);
    scope.querySelectorAll?.('[data-product-gallery]').forEach(gallery => {
      if (gallery.dataset.mediaPlaybackReady === 'true') return;
      gallery.dataset.mediaPlaybackReady = 'true';
      const panels = [...gallery.querySelectorAll('[data-media-id]')];
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const synchronizePlayback = () => {
        panels.forEach(panel => {
          const isActive = !panel.hidden;
          panel.querySelectorAll('video').forEach(video => {
            prepareVideo(video);
            if (!isActive) {
              video.pause();
              return;
            }
            if (reduceMotion.matches) return;
            video.muted = true;
            const playback = video.play();
            if (playback) playback.catch(() => {});
          });
        });
      };

      const observer = new MutationObserver(synchronizePlayback);
      panels.forEach(panel => observer.observe(panel, {
        attributes: true,
        attributeFilter: ['hidden']
      }));

      gallery.addEventListener('click', event => {
        if (!event.target.closest('[data-media-target]')) return;
        window.requestAnimationFrame(synchronizePlayback);
      });

      synchronizePlayback();
    });
  };

  document.addEventListener('contextmenu', event => {
    if (event.target.closest?.('video')) event.preventDefault();
  });
  document.addEventListener('dragstart', event => {
    if (event.target.matches?.('video')) event.preventDefault();
  });

  const start = () => {
    initializeProductMedia(document);
    const mediaObserver = new MutationObserver(records => {
      records.forEach(record => {
        record.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) initializeProductMedia(node);
        });
      });
    });
    mediaObserver.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', event => initializeProductMedia(event.target));
})();


